'use strict';
import {logger} from '../../comments/components/logger';
import {
  IONIC_VIEW_ENTER,
  IONIC_VIEW_LEAVE,
  MODAL_HIDDEN,
  MODAL_SHOWN,
  SCOPE_DESTROY,
} from '../../config/constants/view-events';
import {getId} from '../../core-utilities/domain/ReduxUtils';
import {AudioNotificationService} from '../../core-utilities/notifications/AudioNotificationService';
import {epicErrorHandler, eventToObservable} from '../../core-utilities/rx-utils';


UserMetricsService.$inject = [
  '$rootScope',
  '$timeout',
  'UserDomain',
  AudioNotificationService.SERVICE_NAME,
];

export function UserMetricsService(
  $rootScope,
  $timeout,
  UserDomain,
  audioNotificationService,
) {
  const TIMEOUT = 750; // time from open to mark read in ms

  const NewConversationEvent = 'User:NewConversationEvent';
  const NewPriorityEvent = 'User:NewPriorityEvent';
  let user;
  let lastSeenConversation = null;
  let lastSeenPriority = null;

  this.NewConversationEvent = NewConversationEvent;
  this.NewPriorityEvent = NewPriorityEvent;

  this.init = init;
  this.getLatestConversation = getLatestConversation;
  this.getLatestPriority = getLatestPriority;
  this.updateLatestConversation = updateLatestConversation;
  this.updateLatestPriority = updateLatestPriority;
  this.bindToView = bindToView;
  this.getMetricProps = getMetricProps;
  this.getLatestConversationProps = getLatestConversationProps;
  this.getLatestPriorityProps = getLatestPriorityProps;

  return this;

  function init(currentUser) {
    user = currentUser;

    const sub = UserDomain.$getMetricsRef(getId(user))
      .observe('value')
      .throttleTime(5000, void 0, {leading: true, trailing: true})
      .catch(epicErrorHandler(logger, 'Failed to retrieve user metrics'))
      .subscribe(_onMetricsUpdate);

    return function cleanup() {
      sub.unsubscribe();
    };
  }

  function getLatestConversation() {
    return lastSeenConversation;
  }

  function getLatestPriority() {
    return lastSeenPriority;
  }

  function updateLatestConversation() {
    if (lastSeenConversation && !lastSeenConversation.isRead) {
      $rootScope.$evalAsync(function () {
        $rootScope.$emit(NewConversationEvent, {
          ...lastSeenConversation,
          isRead: true,
        });
      });
      return UserDomain.updateLatestConversation(getId(user));
    } else {
      return Promise.resolve(null);
    }
  }

  function updateLatestPriority() {
    if (lastSeenPriority && !lastSeenPriority.isRead) {
      $rootScope.$evalAsync(function () {
        $rootScope.$emit(NewPriorityEvent, {
          ...lastSeenPriority,
          isRead: true,
        });
      });
      return UserDomain.updateLatestPriority(getId(user));
    } else {
      return Promise.resolve(null);
    }
  }

  /**
   *
   * @param $scope
   * @param property
   * @param isModal=false
   * @param specificId=null
   */
  function bindToView($scope, property, isModal, specificId) {
    if (!$scope || !$scope.$on) throw new Error('UserMetricsService.bindToView expects a $scope');
    specificId = specificId || null;
    const {
      updateMethod,
      getter,
      event,
      timeout,
    } = getMetricProps(property);

    eventToObservable($scope, isModal ? MODAL_SHOWN : IONIC_VIEW_ENTER)
      .throttleTime(1000, void 0, {leading: true, trailing: true})
      .startWith(null)
      .switchMap(() =>
        eventToObservable($rootScope, event).startWith(getter())
          .delay(timeout)
          .filter(metric => specificId ? metric && metric.id === specificId : true)
          .throttleTime(1000, void 0, {leading: true, trailing: true})
          .switchMap(updateMethod)
          .takeUntil(eventToObservable($scope, isModal ? MODAL_HIDDEN : IONIC_VIEW_LEAVE))
      )
      .catch(epicErrorHandler(logger, 'Failed to update user metrics'))
      .takeUntil(eventToObservable($scope, SCOPE_DESTROY))
      .subscribe();
  }

  function getLatestConversationProps() {
    return getMetricProps('latestConversation');
  }

  function getLatestPriorityProps() {
    return getMetricProps('latestPriority');
  }

  function getMetricProps(property) {
    switch (property) {
      case 'latestConversation':
        return {
          updateMethod: updateLatestConversation,
          getter: getLatestConversation,
          event: NewConversationEvent,
          timeout: TIMEOUT,
        };

      case 'latestPriority':
        return {
          updateMethod: updateLatestPriority,
          getter: getLatestPriority,
          event: NewPriorityEvent,
          timeout: 2 * TIMEOUT,
        };

      default:
        throw new Error('UserMetricsService.bindToView expects a valid property and received "' + property + '"');
    }
  }

  /**
   * @param {DataSnapshot} metrics
   * @private
   */
  function _onMetricsUpdate(metrics) {
    const data = metrics && metrics.val();
    let latestConversation = null;
    let latestPriority = null;
    let hasNewItem = false;

    if (data) {
      latestConversation = data.latestConversation;
      latestPriority = data.latestPriority;
    }

    if (!_metricEquals(latestConversation, lastSeenConversation)) {
      $rootScope.$emit(NewConversationEvent, latestConversation);
      if (latestConversation && !latestConversation.isRead) {
        hasNewItem = true;
      }
    }

    if (!_metricEquals(latestPriority, lastSeenPriority)) {
      $rootScope.$emit(NewPriorityEvent, latestPriority);
      if (latestPriority && !latestPriority.isRead) {
        hasNewItem = true;
      }
    }

    lastSeenConversation = latestConversation;
    lastSeenPriority = latestPriority;

    if (hasNewItem) {
      // audioNotificationService.playMessage();
    }
  }

  function _metricEquals(m1, m2) {
    if (!m1 && !m2) return true;
    if (!m1 || !m2) return false;

    if (m1.id !== m2.id) return false;
    if (m1.date !== m2.date) return false;
    if (m1.isRead !== m2.isRead) return false;

    return true;
  }

  function _onError(e) {
    console.warn('Failed to retrieve user metrics');
    console.error(e);
  }
}



// WEBPACK FOOTER //
// ./src/js/auth/services/UserMetricsService.js